"use client";

import { useMemo } from "react";
import { effectiveCapabilities, hasCapability } from "@/lib/targeting/map-content";
import { pickVariant } from "@/lib/targeting/match";
import { useSessionContentContext } from "@/lib/targeting/session-content-context";
import { useMatchContext } from "@/lib/targeting/use-match-context";
import type { WidgetConfig } from "@/lib/widget-config";

export interface PickedGreeting {
  headline: string;
  subheadline: string;
}

/**
 * Greeting variants (capability `greetingVariants`): the session greetingSlot replaces the public
 * one, the first matching variant wins and re-picks on SPA navigation. `undefined` = not in variant
 * mode (the legacy `greeting` applies); `null` = variant mode with no match (no greeting). The texts
 * are rendered by the BE (variables, locale), so they are shown verbatim.
 */
export function useGreetingVariant(config: WidgetConfig): PickedGreeting | null | undefined {
  const session = useSessionContentContext();
  const ctx = useMatchContext();
  const caps = effectiveCapabilities(session.capabilities, config.capabilities);
  const slot = hasCapability(caps, "greetingVariants")
    ? (session.slots?.greetingSlot ?? config.greetingSlot ?? null)
    : null;
  return useMemo(() => {
    if (!slot) return undefined;
    const v = pickVariant(slot.variants, ctx);
    return v ? { headline: v.headline, subheadline: v.subheadline } : null;
  }, [slot, ctx]);
}
